/**
 * Theme configuration for Nadir's Portfolio
 * Creative, artistic design with multiple colors on light/dark themes
 * Inspired by gaming UX and Swiss knife functionality
 */

export const colors = {
  // Primary brand colors - vibrant and creative
  primary: {
    50: '#fefbf3',
    100: '#fef7e0',
    200: '#fdecc8',
    300: '#fbdd96',
    400: '#f8c947',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },
  
  // Secondary colors - warm artistic palette
  secondary: {
    purple: '#d946ef', // Violet plus chaud
    pink: '#f472b6',   // Rose plus chaud
    orange: '#fb923c', // Orange plus vif
    yellow: '#fbbf24', // Jaune plus chaud
    green: '#34d399',  // Vert plus doux
    teal: '#2dd4bf',   // Turquoise plus doux
  },
  
  // Accent colors for highlights and interactive elements
  accent: {
    coral: '#ff8a80',   // Corail plus doux
    mint: '#81c784',    // Menthe plus douce
    lavender: '#ba68c8', // Lavande plus chaude
    peach: '#ffab91',   // Pêche plus vive
    lime: '#aed581',    // Lime plus doux
    cyan: '#4dd0e1',    // Cyan plus doux
  },
  
  // Neutral colors for backgrounds and text
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e5e5e5',
    300: '#d4d4d4',
    400: '#a3a3a3',
    500: '#737373',
    600: '#525252',
    700: '#404040',
    800: '#262626',
    900: '#171717',
  },
  
  // Semantic colors
  success: '#10b981',
  warning: '#f59e0b',
  error: '#ef4444',
  info: '#3b82f6',
};

export const gradients = {
  // Creative gradients for backgrounds and elements
  primary: 'linear-gradient(135deg, #ffd89b 0%, #ff9a9e 100%)', // Jaune vers rose
  sunset: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)', // Rose vers rose clair
  ocean: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', // Crème vers orange
  forest: 'linear-gradient(135deg, #ffeaa7 0%, #fab1a0 100%)', // Jaune vers orange
  fire: 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)', // Rose vers pêche
  cosmic: 'linear-gradient(135deg, #ffd89b 0%, #ff9a9e 50%, #fad0c4 100%)', // Jaune vers rose vers pêche
  aurora: 'linear-gradient(135deg, #ffeaa7 0%, #fab1a0 100%)', // Jaune vers orange
  galaxy: 'linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)', // Jaune doré vers orange rouge
};

export const patterns = {
  // Background patterns with monogram "NB"
  monogram: `
    background-image: 
      url("data:image/svg+xml,%3Csvg width='300' height='300' viewBox='0 0 300 300' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%238b5cf6' fill-opacity='0.15'%3E%3Cpath d='M50 50h20v20h-20z' transform='rotate(15 60 60)'/%3E%3Cpath d='M150 80h15v15h-15z' transform='rotate(-20 157 87)'/%3E%3Cpath d='M250 120h12v12h-12z' transform='rotate(30 256 126)'/%3E%3Cpath d='M80 180h18v18h-18z' transform='rotate(-10 89 189)'/%3E%3Cpath d='M200 200h14v14h-14z' transform='rotate(25 207 207)'/%3E%3Cpath d='M120 250h16v16h-16z' transform='rotate(-35 128 258)'/%3E%3Cpath d='M220 50h13v13h-13z' transform='rotate(45 226 56)'/%3E%3Cpath d='M30 200h17v17h-17z' transform='rotate(-25 38 208)'/%3E%3C/g%3E%3C/svg%3E");
    background-size: 300px 300px;
    background-position: 0 0;
    background-repeat: repeat;
  `,
  
  geometric: `
    background-image: 
      linear-gradient(45deg, rgba(139, 92, 246, 0.04) 25%, transparent 25%),
      linear-gradient(-45deg, rgba(236, 72, 153, 0.04) 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, rgba(59, 130, 246, 0.04) 75%),
      linear-gradient(-45deg, transparent 75%, rgba(16, 185, 129, 0.04) 75%);
    background-size: 60px 60px;
    background-position: 0 0, 0 30px, 30px -30px, -30px 0px;
  `,
  
  dots: `
    background-image: 
      url("data:image/svg+xml,%3Csvg width='350' height='350' viewBox='0 0 350 350' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ec4899' fill-opacity='0.12'%3E%3Ccircle cx='50' cy='50' r='8' transform='rotate(15 50 50)'/%3E%3Ccircle cx='150' cy='100' r='6' transform='rotate(-25 150 100)'/%3E%3Ccircle cx='250' cy='80' r='10' transform='rotate(35 250 80)'/%3E%3Ccircle cx='80' cy='200' r='7' transform='rotate(-15 80 200)'/%3E%3Ccircle cx='200' cy='180' r='9' transform='rotate(40 200 180)'/%3E%3Ccircle cx='120' cy='280' r='5' transform='rotate(-30 120 280)'/%3E%3Ccircle cx='280' cy='250' r='8' transform='rotate(20 280 250)'/%3E%3Ccircle cx='30' cy='150' r='6' transform='rotate(-40 30 150)'/%3E%3Ccircle cx='300' cy='150' r='7' transform='rotate(25 300 150)'/%3E%3C/g%3E%3C/svg%3E");
    background-size: 350px 350px;
    background-position: 0 0;
    background-repeat: repeat;
  `,
  
  waves: `
    background-image: 
      url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%238b5cf6' fill-opacity='0.02'%3E%3Cpath d='M0,20 Q25,10 50,20 T100,20 L100,30 L0,30 Z'/%3E%3Cpath d='M0,70 Q25,60 50,70 T100,70 L100,80 L0,80 Z'/%3E%3Ctext font-family='Poppins, sans-serif' font-size='24' font-weight='700' x='50' y='55' text-anchor='middle' fill='%238b5cf6' fill-opacity='0.03'%3ENB%3C/text%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    background-size: 100px 100px;
    background-repeat: repeat;
  `,
  
  floating: `
    background-image: 
      url("data:image/svg+xml,%3Csvg width='400' height='400' viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23f59e0b' fill-opacity='0.1'%3E%3Cpath d='M50 50 L70 50 L60 70 Z' transform='rotate(15 60 60)'/%3E%3Cpath d='M150 80 L170 80 L160 100 Z' transform='rotate(-20 160 90)'/%3E%3Cpath d='M250 120 L270 120 L260 140 Z' transform='rotate(30 260 130)'/%3E%3Cpath d='M80 180 L100 180 L90 200 Z' transform='rotate(-10 90 190)'/%3E%3Cpath d='M200 200 L220 200 L210 220 Z' transform='rotate(25 210 210)'/%3E%3Cpath d='M120 250 L140 250 L130 270 Z' transform='rotate(-35 130 260)'/%3E%3C/g%3E%3C/svg%3E");
    background-size: 400px 400px;
    background-repeat: repeat;
  `,

  // Patterns thématiques pour chaque section
  about: `
    background-image: 
      url("data:image/svg+xml,%3Csvg width='400' height='400' viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%233b82f6' fill-opacity='0.15'%3E%3C!-- Icônes tech --%3E%3Cpath d='M100 100 L140 100 L140 140 L100 140 Z M120 120 L120 140 M100 120 L140 120' transform='rotate(15 120 120)'/%3E%3Cpath d='M250 150 L290 150 L290 190 L250 190 Z M270 170 L270 190 M250 170 L290 170' transform='rotate(-20 270 170)'/%3E%3Cpath d='M50 250 L90 250 L90 290 L50 290 Z M70 270 L70 290 M50 270 L90 270' transform='rotate(30 70 270)'/%3E%3C!-- Code brackets --%3E%3Cpath d='M180 80 L190 80 L190 120 L180 120 Z M200 80 L210 80 L210 120 L200 120 Z' transform='rotate(-15 195 100)'/%3E%3Cpath d='M320 200 L330 200 L330 240 L320 240 Z M340 200 L350 200 L350 240 L340 240 Z' transform='rotate(25 335 220)'/%3E%3C!-- Formes géométriques --%3E%3Cpath d='M150 50 L170 50 L160 70 Z' transform='rotate(45 160 60)'/%3E%3Ccircle cx='300' cy='80' r='12' transform='rotate(30 300 80)'/%3E%3C/g%3E%3C/svg%3E");
    background-size: 400px 400px;
    background-repeat: repeat;
  `,

  projects: `
    background-image: 
      url("data:image/svg+xml,%3Csvg width='400' height='400' viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23f59e0b' fill-opacity='0.15'%3E%3C!-- Toques de chef avec détails --%3E%3Cpath d='M100 100 L140 100 L135 140 L105 140 Z M115 100 L125 100 M110 120 L130 120 M115 140 L125 140' transform='rotate(-15 120 120)'/%3E%3Cpath d='M250 150 L290 150 L285 190 L255 190 Z M265 150 L275 150 M260 170 L280 170 M265 190 L275 190' transform='rotate(20 270 170)'/%3E%3Cpath d='M50 280 L90 280 L85 320 L55 320 Z M65 280 L75 280 M60 300 L80 300 M65 320 L75 320' transform='rotate(-30 70 300)'/%3E%3C!-- Casseroles avec poignées --%3E%3Cpath d='M180 80 L220 80 A20 20 0 0 1 220 120 L180 120 A20 20 0 0 1 180 80 Z M220 100 L240 100' transform='rotate(15 200 100)'/%3E%3Cpath d='M320 220 L360 220 A18 18 0 0 1 360 256 L320 256 A18 18 0 0 1 320 220 Z M360 238 L380 238' transform='rotate(-25 340 238)'/%3E%3Cpath d='M150 320 L190 320 A16 16 0 0 1 190 352 L150 352 A16 16 0 0 1 150 320 Z M190 336 L210 336' transform='rotate(30 170 336)'/%3E%3C!-- Formes géométriques --%3E%3Cpath d='M200 50 L220 50 L210 70 Z' transform='rotate(45 210 60)'/%3E%3Ccircle cx='80' cy='200' r='12' transform='rotate(30 80 200)'/%3E%3C/g%3E%3C/svg%3E");
    background-size: 400px 400px;
    background-repeat: repeat;
  `,

  tools: `
    background-image: 
      url("data:image/svg+xml,%3Csvg width='400' height='400' viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%2310b981' fill-opacity='0.15'%3E%3C!-- Cuillères --%3E%3Cpath d='M100 100 L110 100 L110 140 L100 140 Z M105 140 L105 160 L110 160' transform='rotate(15 105 130)'/%3E%3Cpath d='M250 150 L260 150 L260 190 L250 190 Z M255 190 L255 210 L260 210' transform='rotate(-20 255 180)'/%3E%3Cpath d='M50 280 L60 280 L60 320 L50 320 Z M55 320 L55 340 L60 340' transform='rotate(30 55 310)'/%3E%3C!-- Fourchettes --%3E%3Cpath d='M180 80 L190 80 L190 120 L180 120 Z M185 80 L185 60 L190 60 L185 65 L185 80' transform='rotate(-15 185 90)'/%3E%3Cpath d='M320 200 L330 200 L330 240 L320 240 Z M325 200 L325 180 L330 180 L325 185 L325 200' transform='rotate(25 325 210)'/%3E%3C!-- Couteaux --%3E%3Cpath d='M150 250 L170 250 L170 270 L150 270 Z M160 250 L160 230 L170 230 L160 240 L160 250' transform='rotate(-30 160 250)'/%3E%3Cpath d='M300 320 L320 320 L320 340 L300 340 Z M310 320 L310 300 L320 300 L310 310 L310 320' transform='rotate(20 310 320)'/%3E%3C!-- Formes géométriques --%3E%3Cpath d='M200 50 L220 50 L210 70 Z' transform='rotate(45 210 60)'/%3E%3Ccircle cx='80' cy='200' r='12' transform='rotate(30 80 200)'/%3E%3C/g%3E%3C/svg%3E");
    background-size: 400px 400px;
    background-repeat: repeat;
  `,

  resources: `
    background-image: 
      url("data:image/svg+xml,%3Csvg width='400' height='400' viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%238b5cf6' fill-opacity='0.15'%3E%3C!-- Livres ouverts --%3E%3Cpath d='M100 100 L140 100 L140 160 L100 160 Z M120 100 L120 160 M100 120 L140 120 M100 140 L140 140' transform='rotate(-15 120 130)'/%3E%3Cpath d='M250 150 L290 150 L290 210 L250 210 Z M270 150 L270 210 M250 170 L290 170 M250 190 L290 190' transform='rotate(20 270 180)'/%3E%3Cpath d='M50 280 L90 280 L90 340 L50 340 Z M70 280 L70 340 M50 300 L90 300 M50 320 L90 320' transform='rotate(-30 70 310)'/%3E%3C!-- Marque-pages avec coins --%3E%3Cpath d='M180 80 L200 80 L200 140 L190 140 L190 120 L180 120 Z' transform='rotate(-15 190 110)'/%3E%3Cpath d='M320 200 L340 200 L340 260 L330 260 L330 240 L320 240 Z' transform='rotate(25 330 230)'/%3E%3Cpath d='M150 320 L170 320 L170 380 L160 380 L160 360 L150 360 Z' transform='rotate(-20 160 350)'/%3E%3C!-- Formes géométriques --%3E%3Cpath d='M200 50 L220 50 L210 70 Z' transform='rotate(45 210 60)'/%3E%3Ccircle cx='80' cy='200' r='12' transform='rotate(30 80 200)'/%3E%3C/g%3E%3C/svg%3E");
    background-size: 400px 400px;
    background-repeat: repeat;
  `,

  contact: `
    background-image: 
      url("data:image/svg+xml,%3Csvg width='450' height='450' viewBox='0 0 450 450' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ec4899' fill-opacity='0.15'%3E%3C!-- Enveloppes de mail --%3E%3Cpath d='M100 100 L160 100 L160 140 L130 140 L100 140 Z M130 140 L160 120' transform='rotate(-15 130 120)'/%3E%3Cpath d='M300 200 L360 200 L360 240 L330 240 L300 240 Z M330 240 L360 220' transform='rotate(20 330 220)'/%3E%3Cpath d='M50 350 L110 350 L110 390 L80 390 L50 390 Z M80 390 L110 370' transform='rotate(-30 80 370)'/%3E%3C!-- Téléphones --%3E%3Cpath d='M200 80 L230 80 L230 140 L200 140 Z M215 80 L215 60 M215 140 L215 160' transform='rotate(15 215 110)'/%3E%3Cpath d='M380 280 L410 280 L410 340 L380 340 Z M395 280 L395 260 M395 340 L395 360' transform='rotate(-25 395 320)'/%3E%3Cpath d='M150 300 L180 300 L180 360 L150 360 Z M165 300 L165 280 M165 360 L165 380' transform='rotate(-20 165 330)'/%3E%3C!-- Formes géométriques --%3E%3Cpath d='M250 50 L270 50 L260 70 L250 70 Z' transform='rotate(45 260 60)'/%3E%3Ccircle cx='80' cy='200' r='12' transform='rotate(30 80 200)'/%3E%3C/g%3E%3C/svg%3E");
    background-size: 450px 450px;
    background-repeat: repeat;
  `
};

export const typography = {
  // Font families
  fonts: {
    primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    heading: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: '"Fira Code", "Monaco", "Consolas", "Liberation Mono", "Courier New", monospace',
  },
  
  // Font sizes
  sizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
    '5xl': '3rem',    // 48px
    '6xl': '3.75rem', // 60px
    '7xl': '4.5rem',  // 72px
  },
  
  // Font weights
  weights: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },
  
  // Line heights
  lineHeights: {
    tight: 1.25,
    snug: 1.375,
    normal: 1.5,
    relaxed: 1.625,
    loose: 2,
  },
};

export const spacing = {
  // Spacing scale
  0: '0',
  1: '0.25rem',   // 4px
  2: '0.5rem',    // 8px
  3: '0.75rem',   // 12px
  4: '1rem',      // 16px
  5: '1.25rem',   // 20px
  6: '1.5rem',    // 24px
  8: '2rem',      // 32px
  10: '2.5rem',   // 40px
  12: '3rem',     // 48px
  16: '4rem',     // 64px
  20: '5rem',     // 80px
  24: '6rem',     // 96px
  32: '8rem',     // 128px
};

export const breakpoints = {
  // Responsive breakpoints
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
};

export const shadows = {
  // Box shadows for depth and layering
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  glow: '0 0 20px rgba(139, 92, 246, 0.3)',
};

export const borderRadius = {
  // Border radius values
  none: '0',
  sm: '0.125rem',   // 2px
  base: '0.25rem',  // 4px
  md: '0.375rem',   // 6px
  lg: '0.5rem',     // 8px
  xl: '0.75rem',    // 12px
  '2xl': '1rem',    // 16px
  '3xl': '1.5rem',  // 24px
  full: '9999px',
};

export const animations = {
  // Animation durations
  duration: {
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
    slower: '1000ms',
  },
  
  // Animation easing functions
  easing: {
    linear: 'linear',
    ease: 'ease',
    easeIn: 'ease-in',
    easeOut: 'ease-out',
    easeInOut: 'ease-in-out',
    bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
};

// Theme variants for light and dark modes
export const lightTheme = {
  colors: {
    ...colors,
    background: colors.neutral[50],
    surface: colors.neutral[100],
    text: colors.neutral[900],
    textSecondary: colors.neutral[600],
    border: colors.neutral[200],
  },
  gradients,
  patterns,
  typography,
  spacing,
  breakpoints,
  shadows,
  borderRadius,
  animations,
};

export const darkTheme = {
  colors: {
    ...colors,
    background: colors.neutral[900],
    surface: colors.neutral[800],
    text: colors.neutral[50],
    textSecondary: colors.neutral[300],
    border: colors.neutral[700],
  },
  gradients,
  patterns,
  typography,
  spacing,
  breakpoints,
  shadows,
  borderRadius,
  animations,
};

export type Theme = typeof lightTheme;
